import type { TrustedAdminRole } from '@/features/auth/staffIdentity';
import type { SidebarItem, UserRole } from './types';

/**
 * WA-4.1 — centralized Web Admin capabilities.
 * Матрица одна и та же для интерфейса и для проверки прав.
 * Authorization uses trusted Staff Identity / superAdmin, never phone or mock role.
 * WA-5.2: wallets.read is operational wallet balance READ, not finance.read.
 * WA-5.6B: users.walletTopUp is CEO/superAdmin only. Manager/Support DENY. Not finance.read.
 * WA-5.7B: users.walletWriteOff is CEO / Manager only. Support DENY. Not finance.read.
 * STEP 27: washes.qr.manage is CEO / Manager only. Support is status read-only.
 * staffPresence.read — блок «Поддержка онлайн» на панели: активность сотрудников
 * нужна руководителю, поддержке в работе не требуется. CEO и Manager, Support — нет.
 * operationalOverview.read — блок «Операционная панель»: сводные цифры по системе
 * за день. Управленческая картина, поддержке не нужна. CEO и Manager, Support — нет.
 * Do not reuse washes.update for QR lifecycle writes.
 */
export type AdminCapability =
  | 'owners.read'
  | 'owners.create'
  | 'owners.update'
  | 'owners.block'
  | 'owners.delete'
  | 'washes.read'
  | 'washes.create'
  | 'washes.update'
  | 'washes.block'
  | 'washes.delete'
  | 'washes.qr.manage'
  | 'ownerWash.assign'
  | 'washAdmins.assign'
  | 'washAdmins.remove'
  | 'users.read'
  | 'users.operationalUpdate'
  | 'users.queueReset'
  | 'users.block'
  | 'users.walletTopUp'
  | 'users.walletWriteOff'
  | 'users.delete'
  | 'wallets.read'
  | 'logs.read'
  | 'staffPresence.read'
  | 'operationalOverview.read'
  | 'finance.read'
  | 'ceoAnalytics.read'
  | 'staff.manage'
  | 'security.manage';

export type CapabilityActor = {
  isSuperAdmin: boolean;
  trustedRole: TrustedAdminRole | null;
};

const ALL_CAPABILITIES: readonly AdminCapability[] = [
  'owners.read',
  'owners.create',
  'owners.update',
  'owners.block',
  'owners.delete',
  'washes.read',
  'washes.create',
  'washes.update',
  'washes.block',
  'washes.delete',
  'washes.qr.manage',
  'ownerWash.assign',
  'washAdmins.assign',
  'washAdmins.remove',
  'users.read',
  'users.operationalUpdate',
  'users.queueReset',
  'users.block',
  'users.walletTopUp',
  'users.walletWriteOff',
  'users.delete',
  'wallets.read',
  'logs.read',
  'staffPresence.read',
  'operationalOverview.read',
  'finance.read',
  'ceoAnalytics.read',
  'staff.manage',
  'security.manage',
];

const CEO_ONLY: ReadonlySet<AdminCapability> = new Set([
  'finance.read',
  'ceoAnalytics.read',
  'staff.manage',
  'security.manage',
  'users.walletTopUp',
  // Удаление пользователя — необратимо, поэтому только у администратора.
  'users.delete',
]);

const SUPPORT_CAPABILITIES: ReadonlySet<AdminCapability> = new Set([
  // Раздел «Владельцы» поддержке не доступен: нет owners.read — нет пункта в меню
  // и нет доступа по прямой ссылке.
  'owners.block',
  'washes.read',
  'washes.block',
  'ownerWash.assign',
  'washAdmins.assign',
  'washAdmins.remove',
  'users.read',
  'users.operationalUpdate',
  'users.queueReset',
  // Блокировка обратима, поэтому доступна и поддержке — как у моек и владельцев.
  'users.block',
  'wallets.read',
  'logs.read',
]);

const MANAGER_CAPABILITIES: ReadonlySet<AdminCapability> = new Set(
  ALL_CAPABILITIES.filter((capability) => !CEO_ONLY.has(capability)),
);

const CEO_CAPABILITIES: ReadonlySet<AdminCapability> = new Set(ALL_CAPABILITIES);

const EMPTY_CAPABILITIES: ReadonlySet<AdminCapability> = new Set();

const SIDEBAR_ITEM_CAPABILITY: Record<SidebarItem, AdminCapability> = {
  dashboard: 'users.read',
  users: 'users.read',
  logs: 'logs.read',
  staff: 'staff.manage',
};

export function capabilitiesForTrustedRole(
  role: TrustedAdminRole | null,
): ReadonlySet<AdminCapability> {
  if (role === 'ceo') return CEO_CAPABILITIES;
  if (role === 'manager') return MANAGER_CAPABILITIES;
  if (role === 'support') return SUPPORT_CAPABILITIES;
  return EMPTY_CAPABILITIES;
}

export function capabilitiesForUserRole(role: UserRole): ReadonlySet<AdminCapability> {
  if (role === 'ceo' || role === 'manager' || role === 'support') {
    return capabilitiesForTrustedRole(role);
  }
  return EMPTY_CAPABILITIES;
}

export function actorHasCapability(
  actor: CapabilityActor,
  capability: AdminCapability,
): boolean {
  if (actor.isSuperAdmin) {
    return CEO_CAPABILITIES.has(capability);
  }
  if (actor.trustedRole === 'ceo') {
    return false;
  }
  return capabilitiesForTrustedRole(actor.trustedRole).has(capability);
}

export function sidebarItemCapability(item: SidebarItem): AdminCapability {
  return SIDEBAR_ITEM_CAPABILITY[item];
}

export function roleHasCapability(role: UserRole, capability: AdminCapability): boolean {
  return capabilitiesForUserRole(role).has(capability);
}
